<!--
  Custom Firebase email action handler for email verification.
  Configure this URL in Firebase Console → Authentication → Templates → Customize action URL.
-->
<template>
    <v-container style="max-width: 600px">
        <CardTitle
            :title="$t('authAction.title')"
            :link="{ href: '/login', text: $t('resetRequest.backToLogin') }"
        />
        <v-card class="pa-5 mt-2" v-auto-animate>
            <v-card-text>
                <div v-auto-animate>
                    <template v-if="status === 'awaitingClick'">
                        <p class="text-body-1 mb-4">{{ $t('authAction.clickToVerify') }}</p>
                        <v-btn
                            color="primary"
                            variant="flat"
                            block
                            @click="handleVerifyEmail(actionCode)"
                        >
                            {{ $t('authAction.verifyButton') }}
                        </v-btn>
                    </template>
                    <v-progress-linear
                        v-else-if="status === 'loading'"
                        indeterminate
                        color="primary"
                        class="mb-4"
                    />
                    <p v-else-if="status === 'success'" class="text-body-1">
                        {{ successMessage }}
                    </p>
                    <p v-else-if="status === 'error'" class="text-body-1 text-error">
                        {{ errorMessage }}
                    </p>
                </div>
            </v-card-text>
            <v-card-actions v-if="status === 'success' && continueUrl">
                <v-btn
                    block
                    color="primary"
                    variant="flat"
                    :href="registrationPendingWithMode"
                >
                    {{ $t("authAction.continue") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
import { applyActionCode } from "firebase/auth";
import { auth } from "@/firebase/config";
import CardTitle from "@/components/registration/CardTitle.vue";

function getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || "";
}

export default {
    name: "AuthAction",
    components: { CardTitle },
    data() {
        return {
            status: "loading",
            successMessage: "",
            errorMessage: "",
            continueUrl: "",
            actionCode: "",
        };
    },
    computed: {
        /**
         * Continue URL with mode=verifyEmail appended so RegistrationPending shows
         * the "email verified" content instead of the default "check your email" content.
         * Our continueUrl is always built without query params (see RegisterForm).
         */
        registrationPendingWithMode() {
            if (!this.continueUrl) return "";
            return `${this.continueUrl}?mode=verifyEmail`;
        },
    },
    mounted() {
        const mode = getQueryParam("mode");
        const actionCode = getQueryParam("oobCode");
        const continueUrlParam = getQueryParam("continueUrl");
        this.continueUrl = continueUrlParam ? decodeURIComponent(continueUrlParam) : "";

        if (!actionCode) {
            this.status = "error";
            this.errorMessage = this.$t("authAction.linkInvalidOrExpired");
            return;
        }

        if (mode === "verifyEmail") {
            this.actionCode = actionCode;
            this.status = "awaitingClick";
        } else if (mode === "resetPassword") {
            // Redirect to Firebase's default handler – Firebase shows its own password reset form
            const projectId = auth.app?.options?.projectId || process.env.VUE_APP_FIREBASE_PROJECT_ID;
            const firebaseHandler = projectId
                ? `https://${projectId}.firebaseapp.com/__/auth/action${window.location.search}`
                : null;
            if (firebaseHandler) {
                window.location.replace(firebaseHandler);
            } else {
                this.status = "error";
                this.errorMessage = this.$t("authAction.linkInvalidOrExpired");
            }
        } else {
            this.status = "error";
            this.errorMessage = this.$t("authAction.linkInvalidOrExpired");
        }
    },
    methods: {
        async handleVerifyEmail(actionCode) {
            if (!actionCode) return;
            this.status = "loading";
            try {
                await applyActionCode(auth, actionCode);
                this.status = "success";
                this.successMessage = this.$t("authAction.emailVerified");
            } catch (err) {
                this.status = "error";
                this.errorMessage = this.$t("authAction.linkInvalidOrExpired");
            }
        },
    },
};
</script>
